import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Check, CheckCheck, Clock, Loader2, MessageCircle, Send, TriangleAlert } from "lucide-react";

import { useChatByPhone, useSendMessage } from "./queries";
import { freeTextBlocked, type ChatMessage } from "./types";

function StatusIcon({ status }: { status: ChatMessage["status"] }) {
  const label =
    status === "queued" ? "Queued" :
    status === "failed" ? "Failed to send" :
    status === "read" ? "Read" :
    status === "delivered" ? "Delivered" : "Sent";
  if (status === "queued") return <Clock className="h-3 w-3" aria-label={label} />;
  if (status === "failed") return <TriangleAlert className="h-3 w-3 text-red-500" aria-label={label} />;
  if (status === "read") return <CheckCheck className="h-3 w-3 text-sky-500" aria-label={label} />;
  if (status === "delivered") return <CheckCheck className="h-3 w-3" aria-label={label} />;
  return <Check className="h-3 w-3" aria-label={label} />;
}

function formatTime(at: string | null) {
  if (!at) return "";
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const outbound = message.direction === "out";
  return (
    <div className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm text-[#111b21] shadow-sm sm:max-w-[75%] ${
          outbound ? "rounded-tr-none bg-[#d9fdd3]" : "rounded-tl-none bg-white"
        }`}
      >
        {message.body ? (
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        ) : (
          <p className="italic text-[#667781]">[{message.message_type}]</p>
        )}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-[#667781]">
          <span>{formatTime(message.at)}</span>
          {outbound && <StatusIcon status={message.status} />}
        </div>
      </div>
    </div>
  );
}

function dateLabel(at: string | null) {
  if (!at) return "";
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

export default function WhatsAppChatPanel({ phone, name }: { phone: string; name?: string }) {
  const { data: chat, isLoading } = useChatByPhone(phone);
  const send = useSendMessage(phone);
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const messages = useMemo(() => chat?.messages || [], [chat]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 144)}px`;
    input.style.overflowY = input.scrollHeight > 144 ? "auto" : "hidden";
  }, [draft]);

  // Meta's Cloud API refuses free text more than 24h after the customer's last
  // message; Baileys instances are not bound by it. See freeTextBlocked.
  const windowClosed = freeTextBlocked(chat);

  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending || windowClosed) return;
    send.mutate(body, { onSuccess: () => setDraft("") });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 bg-[#efeae2] p-4 text-sm text-[#667781]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading conversation...
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#efeae2] text-[#111b21]">
      <div className="flex shrink-0 items-center gap-3 bg-[#f0f2f5] px-4 py-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#d9fdd3] text-[#008069]">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{name || phone}</p>
          <p className="text-xs text-[#667781]">WhatsApp{ name ? ` · ${phone}` : ""}</p>
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-4 py-3 sm:px-6" style={{ backgroundImage: "radial-gradient(circle, #00000009 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
        {messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#667781]">
            No messages yet. The first one you send starts the conversation.
          </p>
        ) : (
          messages.map((message, index) => (
            <Fragment key={message.event_id || message.provider_message_id || index}>
              {dateLabel(message.at) && (index === 0 || dateLabel(message.at) !== dateLabel(messages[index - 1].at)) && (
                <div className="flex justify-center py-3">
                  <span className="rounded-lg bg-white px-3 py-1 text-[11px] text-[#667781] shadow-sm">{dateLabel(message.at)}</span>
                </div>
              )}
              <MessageBubble message={message} />
            </Fragment>
          ))
        )}
        <div ref={endRef} />
      </div>

      {send.isError && (
        <p role="alert" className="px-3 py-2 text-xs text-red-600">
          {(send.error as Error)?.message || "Could not send the message."}
        </p>
      )}

      {windowClosed ? (
        <p className="shrink-0 bg-[#f0f2f5] px-4 py-3 text-xs text-[#667781]">
          The 24-hour reply window has closed. WhatsApp only allows an approved
          template until this contact messages again.
        </p>
      ) : (
        <div className="flex shrink-0 items-end gap-2 bg-[#f0f2f5] p-2.5">
          <textarea
            ref={inputRef}
            rows={1}
            className="min-h-11 min-w-0 flex-1 resize-none overflow-x-hidden rounded-2xl border border-transparent bg-white px-4 py-3 text-sm leading-5 text-[#111b21] placeholder:text-[#667781] focus:border-[#00a884] focus:outline-none"
            aria-label="WhatsApp message"
            placeholder="Type a message"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || send.isPending}
            aria-label="Send WhatsApp message"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white hover:bg-[#06cf9c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#06cf9c] disabled:opacity-40"
          >
            {send.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      )}
    </div>
  );
}
